import { useLocation, useNavigate } from 'react-router-dom';
import {
  ServingPlatterIcon,
  MegaphoneIcon,
  ForkIcon,
  GearIcon,
  PagesIcon,
  PaletteIcon,
  LogoutIcon,
  LocationPinIcon
} from '../../assets/svgs/icons';
import { RESTAURANT_PROFILE_SIDEBAR_ITEMS } from '../../constants/restaurantProfileSidebar';
import { BRAND_PROFILE_SIDEBAR_ITEMS } from '../../constants/brandProfileSidebar';
import {
  PAGE_BRAND_PROFILE,
  PAGE_DESIGN_COLORS,
  PAGE_DESIGN_FONTS,
  PAGE_DISCOVERY_CONTENT,
  PAGE_EMAIL_MANAGER,
  PAGE_LOCATIONS,
  PAGE_LOCATION_PROFILE,
  PAGE_MENUS,
  PAGE_PAGES,
  PAGE_PRINTING_MATERIALS,
  PAGE_COMING_SOON,
  PAGE_PROMOTIONS,
  PAGE_SETTINGS_ACCOUNT
} from '../../constants/UriConstants';
import { useAuthContext } from '../../contexts/AuthContext';
import { useBrandContext } from '../../contexts/BrandContext';
import { usePermissionsContext } from '../../contexts/PermissionsContext';
import SidebarLink from './SidebarLink';
import SidebarSubmenu from './SidebarSubmenu';
import SidebarSubmenuItem from './SidebarSubmenuItem';
import {
  isRestaurantProfileSidebarItemActive,
  isSidebarSubmenuExpanded,
  useSidebarNavigation
} from './sidebarNavigation';
import { useRestaurantContext } from '../../contexts/RestaurantContext';

interface SidebarNavLinksProps {
  onNavigate?: () => void;
}

const SidebarNavLinks = ({ onNavigate }: SidebarNavLinksProps) => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { removeToken } = useAuthContext();
  const { currentBrand } = useBrandContext();
  const { clearUserPermissions } = usePermissionsContext();
  const nav = useSidebarNavigation();

  const brandId = currentBrand?.id;

  const handleLinkClick = onNavigate;

  const handleMarketingAction = () => {
    navigate(`/${PAGE_PROMOTIONS}`);
    onNavigate?.();
  };
  const handleDesignAction = () => {
    navigate(`/${PAGE_DESIGN_COLORS}`);
    onNavigate?.();
  };
  const handleLogout = () => {
    onNavigate?.();
    removeToken?.();
    clearUserPermissions?.();
  };

  const { currentRestaurant } = useRestaurantContext();

  return (
    <ul className="sidebar-link-container">
      <SidebarLink
        name="BRAND PROFILE"
        icon={<ServingPlatterIcon />}
        isActive={nav.brandProfile}
        isExpanded={isSidebarSubmenuExpanded(pathname, 'brandProfile')}
        onAction={() => {
          navigate(`/${brandId}/${PAGE_BRAND_PROFILE}`);
          onNavigate?.();
        }}
      >
        {isSidebarSubmenuExpanded(pathname, 'brandProfile') && (
          <SidebarSubmenu>
            {BRAND_PROFILE_SIDEBAR_ITEMS.map((item) => (
              <SidebarSubmenuItem
                key={item.id}
                name={item.label}
                path={`/${brandId}/${PAGE_BRAND_PROFILE}/${item.path}`}
                isActive={pathname.includes(`/${PAGE_BRAND_PROFILE}/${item.path}`)}
                onClick={handleLinkClick}
              />
            ))}
          </SidebarSubmenu>
        )}
      </SidebarLink>
      <SidebarLink
        name="LOCATIONS"
        path={`/${brandId}/${PAGE_LOCATIONS}`}
        icon={<LocationPinIcon />}
        isActive={pathname.includes(PAGE_LOCATIONS)}
        onClick={handleLinkClick}
      />
      {nav.locationProfile && (
        <SidebarLink
          name="LOCATION PROFILE"
          icon={<ServingPlatterIcon />}
          isActive={nav.locationProfile}
          isExpanded={isSidebarSubmenuExpanded(pathname, 'locationProfile')}
          onAction={() => {
            navigate(
              currentRestaurant?.restaurantID
                ? `/${PAGE_LOCATION_PROFILE}/${currentRestaurant.restaurantID}`
                : `/${PAGE_LOCATION_PROFILE}`
            );
            onNavigate?.();
          }}
        >
          {isSidebarSubmenuExpanded(pathname, 'locationProfile') && (
            <SidebarSubmenu>
              {RESTAURANT_PROFILE_SIDEBAR_ITEMS.map((item) => {
                const locationProfileItemIds = [
                  'basic-information',
                  'address',
                  'hours',
                  'reservations-ordering',
                  'online-ordering',
                  'menus'
                ];

                const itemPath =
                  locationProfileItemIds.includes(item.id) && currentRestaurant?.restaurantID
                    ? `/${currentRestaurant.restaurantID}/${item.path.replace(/^\//, '')}`
                    : item.path;

                return (
                  <SidebarSubmenuItem
                    key={item.id}
                    name={item.label}
                    path={itemPath}
                    isActive={isRestaurantProfileSidebarItemActive(pathname, itemPath)}
                    onClick={handleLinkClick}
                  />
                );
              })}
            </SidebarSubmenu>
          )}
        </SidebarLink>
      )}
      <SidebarLink
        name="PAGES"
        path={`/${PAGE_PAGES}`}
        icon={<PagesIcon />}
        isActive={nav.pages}
        onClick={handleLinkClick}
      />
      <SidebarLink
        name="MARKETING"
        icon={<MegaphoneIcon />}
        isActive={nav.marketing}
        isExpanded={isSidebarSubmenuExpanded(pathname, 'marketing')}
        onAction={handleMarketingAction}
      >
        {isSidebarSubmenuExpanded(pathname, 'marketing') && (
          <SidebarSubmenu>
            <SidebarSubmenuItem
              name="PROMOTIONS"
              path={`/${PAGE_PROMOTIONS}`}
              isActive={nav['marketing.promotions']}
              onClick={handleLinkClick}
            />
            <SidebarSubmenuItem
              name="EMAILS"
              path={`/${PAGE_EMAIL_MANAGER}`}
              isActive={nav['marketing.emails']}
              onClick={handleLinkClick}
            />
            <SidebarSubmenuItem
              name="DISCOVERY"
              path={`/${PAGE_DISCOVERY_CONTENT}`}
              isActive={nav['marketing.discovery']}
              onClick={handleLinkClick}
            />
            <SidebarSubmenuItem
              name="PRINTING MATERIALS"
              path={`/${PAGE_PRINTING_MATERIALS}`}
              isActive={nav['marketing.printingMaterials']}
              onClick={handleLinkClick}
            />
            <SidebarSubmenuItem
              name="COMING SOON"
              path={`/${PAGE_COMING_SOON}`}
              isActive={nav['marketing.comingSoon']}
              onClick={handleLinkClick}
            />
          </SidebarSubmenu>
        )}
      </SidebarLink>
      <SidebarLink
        name="DESIGN"
        icon={<PaletteIcon />}
        isActive={nav.design}
        isExpanded={isSidebarSubmenuExpanded(pathname, 'design')}
        onAction={handleDesignAction}
      >
        {isSidebarSubmenuExpanded(pathname, 'design') && (
          <SidebarSubmenu>
            <SidebarSubmenuItem
              name="COLORS"
              path={`/${PAGE_DESIGN_COLORS}`}
              isActive={nav['design.colors']}
              onClick={handleLinkClick}
            />
            <SidebarSubmenuItem
              name="FONTS"
              path={`/${PAGE_DESIGN_FONTS}`}
              isActive={nav['design.fonts']}
              onClick={handleLinkClick}
            />
          </SidebarSubmenu>
        )}
      </SidebarLink>
      <SidebarLink
        name="SETTINGS"
        path={`/${PAGE_SETTINGS_ACCOUNT}`}
        icon={<GearIcon />}
        isActive={nav.settings}
        onClick={handleLinkClick}
      />
      <SidebarLink name="LOGOUT" icon={<LogoutIcon />} onAction={handleLogout} />
    </ul>
  );
};

export default SidebarNavLinks;
